import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'salon_session';
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Session helpers — store user + lastActivity + expiresAt
// Note: PIN-based POS — no tokens/passwords stored, only user profile + permissions
const saveSession = (userData, timeoutMs) => {
  const now = Date.now();
  // Strip any sensitive fields before storing
  const safeUser = { ...userData };
  delete safeUser.pin;
  delete safeUser.password;
  delete safeUser.token;
  const session = {
    user: safeUser,
    lastActivity: now,
    expiresAt: now + timeoutMs,
    timeoutMs,
  };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Fallback to localStorage if sessionStorage unavailable
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }
  localStorage.removeItem('salon_user');
};

const loadSession = () => {
  // Check sessionStorage first, then localStorage
  const raw = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
  if (raw) {
    try {
      const session = JSON.parse(raw);
      if (session?.user?.id && session?.user?.name) {
        const now = Date.now();
        const timeoutMs = session.timeoutMs || DEFAULT_TIMEOUT_MS;
        const lastActivity = session.lastActivity || 0;
        if (now > (session.expiresAt || 0) || (now - lastActivity) > timeoutMs) {
          clearSession();
          return null;
        }
        return session;
      }
    } catch {
      // Corrupted
    }
    clearSession();
  }
  // Migrate legacy salon_user (one-time, force re-login)
  const legacy = localStorage.getItem('salon_user');
  if (legacy) {
    localStorage.removeItem('salon_user');
  }
  return null;
};

const clearSession = () => {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('salon_user');
};

const touchSession = (timeoutMs) => {
  const raw = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
  if (!raw) return;
  try {
    const session = JSON.parse(raw);
    const now = Date.now();
    session.lastActivity = now;
    session.expiresAt = now + timeoutMs;
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  } catch (e) {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    inactivity_timeout_value: 30,
    inactivity_timeout_unit: 'minutes'
  });
  const lastActivityRef = useRef(Date.now());

  // Load session on mount — validates expiry
  useEffect(() => {
    const session = loadSession();
    if (session) {
      setUser(session.user);
      lastActivityRef.current = session.lastActivity || Date.now();
    }
    setLoading(false);
  }, []);

  // Load system settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings/system`);
        if (response.data && typeof response.data === 'object') {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        // Non-critical — keep default settings
      }
    };
    fetchSettings();
  }, []);

  // Calculate timeout in milliseconds
  const getTimeoutMs = useCallback(() => {
    const value = settings.inactivity_timeout_value || 30;
    const unit = settings.inactivity_timeout_unit || 'minutes';
    switch (unit) {
      case 'minutes':
        return value * 60 * 1000;
      case 'hours':
        return value * 60 * 60 * 1000;
      case 'days':
        return value * 24 * 60 * 60 * 1000;
      default:
        return DEFAULT_TIMEOUT_MS;
    }
  }, [settings]);

  // Track user activity — persist to localStorage
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    touchSession(getTimeoutMs());
  }, [getTimeoutMs]);

  // Check for inactivity and auto-logout
  useEffect(() => {
    if (!user) return;

    const checkInactivity = () => {
      const timeoutMs = getTimeoutMs();
      const now = Date.now();
      if (now - lastActivityRef.current > timeoutMs) {
        logout(true);
      }
    };

    const interval = setInterval(checkInactivity, 10000);

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      clearInterval(interval);
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, getTimeoutMs, updateActivity]);

  const login = async (pin) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/pin-login`, { pin });
      const userData = response.data.user;
      setUser(userData);
      const timeoutMs = getTimeoutMs();
      saveSession(userData, timeoutMs);
      lastActivityRef.current = Date.now();
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid PIN'
      };
    }
  };

  const logout = async (isAutoLogout = false) => {
    if (user) {
      try {
        await axios.post(`${API_URL}/api/auth/logout`, {
          user_id: user.id,
          user_name: user.name
        });
      } catch (error) {
        // ignore
      }
    }
    setUser(null);
    clearSession();
  };

  const logActivity = async (actionType, description, appointmentId = null, details = null) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/api/activity`, {
        action_type: actionType,
        description,
        user_id: user.id,
        user_name: user.name,
        appointment_id: appointmentId,
        details
      });
    } catch (error) {
      // non-critical
    }
  };

  const refreshSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/system`);
      if (response.data && typeof response.data === 'object') {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // non-critical
    }
  };

  const value = {
    user,
    loading,
    settings,
    login,
    logout,
    logActivity,
    refreshSettings,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    canManageSettings: user?.is_admin && (user?.can_manage_settings !== false),
    canManageServices: user?.is_admin && (user?.can_manage_services !== false),
    canManageUsers: user?.is_admin && (user?.can_manage_users !== false),
    canManageSchedules: user?.is_admin && (user?.can_manage_schedules !== false),
    canManageOnlineBooking: user?.is_admin && (user?.can_manage_online_booking !== false),
    canEditPayments: user?.can_edit_payments !== false,
    canManageClients: user?.can_manage_clients !== false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
